import classNames from "classnames";
import { parse as optimisticParse } from "best-effort-json-parser";
import React, { ReactElement, useState, useCallback } from "react";
import { AiSummaryProps, AiSummaryState } from "./AiSummary.types";
import { localize } from "src/l10n";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { MediumBodyBolder, SmallBody } from "../Text";
import { Conditional } from "../Conditional";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useSelector } from "react-redux";
import RainbowButton from "src/assistant/components/RainbowButton/RainbowButton";
import DOMPurify from 'dompurify';

/**
 * AiSummary component
 *
 * @param {AiSummaryProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function AiSummary({
    title,
    content,
    overridenContent,
    onSummary
}: AiSummaryProps): ReactElement | null {
    const [state, setState] = useState<AiSummaryState>({
        generating: false,
    });

    const enabled = useSelector<Spintr.AppState, boolean>(
        (appState) => appState.instance.get("enableAiSummary") as boolean);

    const { generating, requested, result } = state;

    const onClicked = useCallback(() => {
        if (requested) {
            return;
        }

        setState((prevState) => ({
            ...prevState,
            requested: true,
            generating: true,
        }));

        let json = "";
        const abortController = new AbortController();
        fetchEventSource("/api/v1/insights/article-summary", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                title, content
            }),
            signal: abortController.signal,

            onerror: (err) => {
                console.log(err);
                abortController.abort();
                setState((prevState) => ({
                    ...prevState,
                    generating: false,
                    result: undefined,
                }));
                throw err;
            },

            onmessage: (event) => {
                const text = event.data;
                json += text;

                setState((prevState) => ({
                    ...prevState,
                    result: optimisticParse(json),
                }));
            },

            onopen: async (response) => {
                if (response.ok) {
                    return;
                }

                throw new Error(`Failed to open connection: ${response.status} ${response.statusText}`);
            },

            onclose: () => {
                const result = optimisticParse(json);

                setState((prevState) => ({
                    ...prevState,
                    generating: false,
                    result: result,
                }));

                if (result?.summaryAndHTMLList && onSummary) {
                    onSummary(result.summaryAndHTMLList);
                }
            },
        });
    }, [content, requested, setState, title]);

    if (!enabled || (requested && !generating && !result?.summaryAndHTMLList)) {
        return null;
    }

    if (!content || content.length < 200) {
        return null;
    }

    return (
        <div className={classNames("AiSummary", {
            "expanded": result?.summaryAndHTMLList?.length > 0,
        })}>
            <div className="AiSummary-header">
                <div className="left">
                    <Visage2Icon icon="sparkling-fill" type="custom" color="transparent" />
                    <MediumBodyBolder color="contentDark">
                        {localize("AI_SUMMARY")}
                    </MediumBodyBolder>
                </div>
                <Conditional condition={!requested || generating}>
                    <RainbowButton
                        text={localize("SHOW_AI_SUMMARY")}
                        onClick={onClicked}
                        isLoading={generating}
                        isEnabled={true}
                        onClickData={null}
                    />
                </Conditional>
            </div>
            <div className="AiSummary-content">
                <SmallBody className="summary-body"
                           color="contentDark">
                    <div dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(overridenContent ?? result?.summaryAndHTMLList ?? "", {
                            ALLOWED_TAGS: ['br', 'ul', 'li', 'ol', 'strong']
                        })
                    }} />
                </SmallBody>
            </div>
        </div>
    );
}

export default AiSummary;

